import React, { useState } from "react"

import { useTranslation } from "react-i18next"

import { Grid } from "@mui/material"
import { Pagination, Tag } from "@synapse-analytics/synapse-ui"
import type { MRT_ColumnDef } from "material-react-table"

import { BaseTable } from "../../../../components/tables/BaseTable"
import type { FuzzyMatchResult } from "../../../../types/generated/api/FuzzyMatchResult"
import { useFuzzyMatchResults } from "../../hooks/useFuzzyMatchResults"

import styles from "./FuzzyMatchTable.module.scss"

/**
 * Component that displays fuzzy match results in a table format.
 * Shows fuzzy matching information including original text, mapped words,
 * success status, and match results for a specific project and optional prediction.
 *
 * @param props - Component props
 * @param props.projectId - The UUID of the project
 * @param props.prediction_uuid - Optional UUID of a specific prediction to filter by
 * @returns JSX element containing the fuzzy match results table
 */
export function FuzzyMatchResultsTable({
  projectId,
  prediction_uuid,
}: {
  projectId: string
  prediction_uuid?: string
}): JSX.Element {
  const { t } = useTranslation("decisions", { keyPrefix: "fuzzyMatchResultsTable" })
  const rowsPerPage = 10
  const [page, setPage] = useState(1)

  const { data, isLoading } = useFuzzyMatchResults({
    projectId,
    prediction_uuid: prediction_uuid,
    page,
    pageSize: rowsPerPage,
  })

  const records: FuzzyMatchResult[] = data?.data.results ?? []
  const totalCount = data?.data.count ?? 0

  const columns: MRT_ColumnDef<FuzzyMatchResult>[] = [
    { header: t("originWord"), accessorKey: "original_text" },
    { header: t("mappedWords"), accessorKey: "closest_match" },
    {
      header: t("fuzzySuccessful"),
      accessorKey: "is_fuzzy_successful",
      Cell: ({ cell }) =>
        cell.getValue<boolean>() ? (
          <Tag size="small" color="green" variant="filled">
            {t("success")}
          </Tag>
        ) : (
          <Tag size="small" color="red" variant="filled">
            {t("failure")}
          </Tag>
        ),
    },
    {
      header: t("matched"),
      accessorKey: "is_matched",
      Cell: ({ cell }) => (cell.getValue<boolean>() ? t("yes") : t("no")),
    },
    { header: t("failureReason"), accessorKey: "fuzzy_failure_reason" },
  ]

  return (
    <Grid container direction="column" gap={2} xs>
      <Grid item className={styles.tableWrapper}>
        <BaseTable
          newStyling
          disablePagination
          data={records}
          columns={columns as MRT_ColumnDef[]}
          title={t("title")}
          isLoading={isLoading}
          pageSize={rowsPerPage}
          enableOrdering={false}
          disableToolbar
        />
      </Grid>

      {totalCount > rowsPerPage && (
        <Pagination
          page={page}
          onPageChange={(p: number) => setPage(p)}
          pageSize={rowsPerPage}
          totalCount={totalCount}
          variant="simple"
          size="small"
          hidePageSize
          maxVisiblePages={5}
        />
      )}
    </Grid>
  )
}
